import { AppThunk } from "../store";
import {
    setAuthorityMappings,
    setData,
    setDefectCodes,
    setError,
    setFilteredDefects,
    setLoading,
    updateData,
    setRegimeMappings,
    setRegimeDescriptions,
    setRegimeMappingsLoading,
    setRegimeMappingsLoaded,
    setRegimeMappingsError
} from "../slices/defectSlice";
import { DefectStatus } from "../../types/DefectStatus";
import { getDefectCodes, getDefects } from "../../services/DefectService";
import { getRegimeMappings } from "../../services/RegimeMappingService";
import {
    buildAuthorityMappings,
    buildRegimeDescriptions
} from "../../utils/PscRiskGraphHelper";
import { createRectification } from "./rectificationThunks";
import {
    selectDefects,
    selectFilteredDefects
} from "../selectors/defectSelectors";
import { getDefectRefectificationPlan } from "../../services/DefectRefectificationService";
import { selectInspectionAndGradeData } from "../selectors/fleetSelectors";
import { constructDefectData } from "../../utils/DefectsTableDataHelper";
import { applyGlobalFilter } from "../../components/Shared/GlobalFilter";
import { selectGlobalFilters } from "../selectors/filtersSelectors";
import { getInspectionIdsForDefects } from "../../utils/DefectTableHelper";
import { selectUserHasCapability } from "../selectors/userSelectors";
import { Capability } from "../../types/Capability";
import { setEvidenceLoading } from "../slices/rectificationSlice";
import { selectHasEvidence } from "../selectors/rectificationSelector";
import { getDefectEvidenceValue, mapRectifiedEvidence } from "../../utils/DefectRectificationHelpers";
import { DefectDataItem } from "../../types/DefectsDataItem";
import { DefectRef, findDefect } from "../../utils/DefectIdentity";

export const fetchDefects =
    (): AppThunk<Promise<boolean>> =>
    async (dispatch, getState) => {
        const state = getState();

        const hasCapability = selectUserHasCapability(
            state,
            Capability.CAP_FLEETAPP_VIEW_DEFECTS
        );
        
        if (hasCapability) {
            dispatch(setLoading(true));
            dispatch(setError(false));

            try {
                const inspectionsData = selectInspectionAndGradeData(
                    state
                );

                const inspectionIds = getInspectionIdsForDefects(inspectionsData);

                if (inspectionIds.length) {
                    const response = await getDefects(inspectionIds);

                    if (response) {
                        dispatch(setData(response.defects));
                    } else {
                        throw Error("no data");
                    }
                }
            } catch (e) {
                console.error(e);
                dispatch(setError(true));
            } finally {
                dispatch(setLoading(false));
            }
        }

        return true;
    };

export const updateDefectItem =
    (
        defect: DefectRef,
        columnName: string,
        value: unknown
    ): AppThunk<Promise<boolean>> =>
    async (dispatch, getState) => {
        const state = getState();
        const filteredDefects = selectFilteredDefects(state);
        const hasEvidence = selectHasEvidence(state);

        const matchingDefect = findDefect(filteredDefects, defect);

        if (matchingDefect) {
            dispatch(setLoading(true));

            const updatedDefect = { ...matchingDefect, [columnName]: value };
            updatedDefect.evidence = getDefectEvidenceValue(hasEvidence);

            if (columnName === "status" && value === DefectStatus.rectified) {
                if (updatedDefect.rectificationId) {
                    const matchingRectification =
                        await getDefectRefectificationPlan(
                            updatedDefect.inspectionId,
                            updatedDefect.defectId
                        );

                    if (!matchingRectification.resolutionDate) {
                        const formData = {
                            resolutionDate: new Date()
                        };

                        await dispatch(
                            createRectification(formData, updatedDefect)
                        );
                    }
                } else {
                    const formData = {
                        actionNotes: "",
                        resolutionDate: new Date(),
                        actualCost: null,
                        costArea: null,
                        defectStatus: updatedDefect.status
                    };

                    await dispatch(
                        createRectification(formData, updatedDefect)
                    );
                }
            }

            dispatch(updateData([updatedDefect]));

            dispatch(setLoading(false));

            return true;
        }

        return false;
    };

export const setupFilteredDefects =
    (initialSetup?: boolean): AppThunk =>
    (dispatch, getState) => {
        const state = getState();
        const defects = selectDefects(state);
        const inspectionAndGradeData = selectInspectionAndGradeData(state);
        const globalFilters = selectGlobalFilters(state);

        let data = defects;

        if (initialSetup) {
            data = constructDefectData(
                defects,
                inspectionAndGradeData.fleetDataItems
            );

            data = data.map((defect) => {
                const inspectionData =
                    inspectionAndGradeData.fleetDataItems.find(
                        (item) => item.inspection?.guid === defect.inspectionId
                    );
                const inspectionNumber =
                    inspectionData?.inspection?.inspectionNumber;
                defect = { ...defect, inspectionNumber };
                return defect;
            });

            dispatch(setData(data));
            dispatch(fetchDefectsEvidence());
            dispatch(fetchRegimeMappings());
        }
        

        data = data.filter((item) =>
            applyGlobalFilter(
                globalFilters,
                item.vessel.vesselName,
                item.vessel.type,
                item.technicalManager,
                item.vessel.shipAge
            )
        );

        dispatch(setFilteredDefects(data));
    };

export const fetchDefectsEvidence =
    (): AppThunk => async (dispatch, getState) => {
        const defects = selectDefects(getState());

        const defectsWithEvidence = (await mapRectifiedEvidence(
            defects
        )) as DefectDataItem[];

        dispatch(updateData(defectsWithEvidence));
        dispatch(setEvidenceLoading(false));
    };

/**
 * Loaded together because they share one cached call, and because loading them
 * separately meant the chips on the defects table had no descriptions until
 * the PSC Risk tab had been visited.
 */
export const fetchRegimeMappings =
    (): AppThunk => async (dispatch) => {
        dispatch(setRegimeMappingsLoading(true));
        dispatch(setRegimeMappingsError(false));

        try {
            const mappings = await getRegimeMappings();

            dispatch(setRegimeMappings(mappings));
            dispatch(setRegimeDescriptions(buildRegimeDescriptions(mappings)));
            dispatch(setAuthorityMappings(buildAuthorityMappings(mappings)));
        } catch (error) {
            // Nothing is dispatched: writing a fresh {} would change the state
            // reference and re-trigger the effect that called this, looping
            // against an endpoint that is already failing.
            console.error("Failed to fetch regime mappings:", error);
            dispatch(setRegimeMappingsError(true));
        } finally {
            dispatch(setRegimeMappingsLoaded(true));
            dispatch(setRegimeMappingsLoading(false));
        }
    };

export const fetchDefectCodes =
    (): AppThunk => async (dispatch) => {
        try {
            const defectCodes = await getDefectCodes();
            dispatch(setDefectCodes(defectCodes));
        } catch (error) {
            console.error("Failed to fetch defect codes:", error);
            dispatch(setDefectCodes([]));
        }
    };
